"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { onAuthStateChanged, User } from "firebase/auth";
import { collection, query, where, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { Loader2, Send } from "lucide-react";
import { Countdown } from "@/components/countdown";
const EmojiEventsIcon = t0 => {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg viewBox="0 0 24 24" className={className} fill="#FFD700">{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export default function FeedbackPage() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmittedRecently, setHasSubmittedRecently] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [resetDate, setResetDate] = useState<Date | null>(null);
  const router = useRouter();
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      setUser(currentUser);
      if (!currentUser) {
        setLoading(false);
        router.push("/account");
        return;
      }
      await checkSubmissionStatus(currentUser.uid);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [router]);
  const checkSubmissionStatus = async (uid: string) => {
    try {
      const feedbackRef = collection(db, "dbFeedback");
      const q = query(feedbackRef, where("userId", "==", uid));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        setHasSubmittedRecently(false);
        return;
      }
      let latestTimestamp = 0;
      querySnapshot.forEach(doc => {
        const data = doc.data();
        if (data.timestamp) {
          const ts = data.timestamp.toMillis ? data.timestamp.toMillis() : new Date(data.timestamp).getTime();
          if (ts > latestTimestamp) latestTimestamp = ts;
        }
      });
      if (latestTimestamp === 0) {
        setHasSubmittedRecently(false);
        return;
      }
      const lastSubmissionDate = new Date(latestTimestamp);
      const nextAllowedDate = new Date(lastSubmissionDate.getTime() + 7 * 24 * 60 * 60 * 1000);
      const now = new Date();
      if (now < nextAllowedDate) {
        setHasSubmittedRecently(true);
        setResetDate(nextAllowedDate);
      } else {
        setHasSubmittedRecently(false);
      }
    } catch (error) {
      console.error("Error checking submission status:", error);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!suggestion.trim() || !user) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, "dbFeedback"), {
        userId: user.uid,
        userEmail: user.email,
        suggestion: suggestion.trim(),
        timestamp: serverTimestamp()
      });
      const now_0 = new Date();
      const nextAllowedDate_0 = new Date(now_0.getTime() + 7 * 24 * 60 * 60 * 1000);
      setResetDate(nextAllowedDate_0);
      setHasSubmittedRecently(true);
      setSuggestion("");
    } catch (error_0) {
      console.error("Error submitting feedback:", error_0);
      alert("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-transparent">
         <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#B0D18B]"></div>
       </div>;
  }
  if (!user) {
    return null;
  }
  return <div className="animate-in fade-in duration-500">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-sriracha tracking-tighter text-[#B0D18B] mb-4 opacity-0 blur-md translate-y-8" style={{
          animation: "fadeSlideUnblur 1s ease-out 0.6s forwards"
        }}>
            Feedback
          </h1>
          <p className="text-[#C4C8BA] opacity-0 blur-md translate-y-8 max-w-2xl mx-auto" style={{
          animation: "fadeSlideUnblur 1s ease-out 0.8s forwards"
        }}>
            Suggest improvements and be part of making MVPlayer better.
          </p>
        </div>
        
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 opacity-0 blur-md translate-y-8 hover:border-[#B0D18B] transition-colors duration-200" style={{
          animation: "fadeSlideUnblur 1s ease-out 0.8s forwards"
        }}>
            {hasSubmittedRecently && resetDate ? <div className="text-center py-8">
                <div className="bg-[#B0D18B]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <EmojiEventsIcon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-[#E2E3D8] font-sriracha mb-2">
                  Thanks for your suggestion!
                </h3>
                <p className="text-[#C4C8BA] mb-8">
                  We've received your feedback. You can submit another suggestion in:
                </p>
                
                <div className="flex justify-center mb-12">
                  <Countdown targetDate={resetDate} />
                </div>

                <div className="border-t border-[#8E9285]/20 pt-8 mt-8">
                  <p className="text-[#E2E3D8] font-semibold mb-4 font-sriracha text-lg">
                    Loving MVPlayer?
                  </p>
                  <p className="text-[#C4C8BA] text-sm mb-6 max-w-md mx-auto">
                    While you wait, why not leave us a review on the Google Play Store? Your support helps us grow!
                  </p>
                  <a href="https://play.google.com/store/apps/details?id=eu.mvplayer.mvplayer" target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-90 transition-opacity">
                    <Image src="/drawables/google-play-badge.svg" alt="Review on Google Play" width={161} height={48} className="h-12 w-auto" />
                  </a>
                </div>
              </div> : <form onSubmit={handleSubmit} className="space-y-6">
                 <div>
                  <label htmlFor="suggestion" className="block text-sm font-medium text-[#E2E3D8] mb-2 font-geist">
                    Your Suggestion
                  </label>
                  <textarea id="suggestion" rows={6} className="w-full bg-[#12140E] border border-[#8E9285]/20 rounded-xl p-4 text-[#E2E3D8] placeholder-[#5C5F56] focus:outline-none focus:border-[#B0D18B] transition-colors font-geist resize-none" placeholder="Describe the one thing you'd like to see to make MVPlayer better..." value={suggestion} onChange={e_0 => setSuggestion(e_0.target.value)} required />
                  <p className="mt-2 text-xs text-[#8E9285] text-right">
                    To keep the quality of suggestions high, we limit submissions to one per week. Make it count!
                  </p>
                </div>

                <button type="submit" disabled={submitting || !suggestion.trim()} className="w-full bg-[#B0D18B] hover:bg-[#9DC077] text-[#1A1C16] font-bold py-4 px-6 rounded-xl transition-all shadow-lg hover:shadow-[#B0D18B]/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-geist">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>
                      <span>Submit Suggestion</span>
                      <Send className="h-4 w-4" />
                    </>}
                </button>
              </form>}
          </div>
        </div>
      </section>
    </div>;
}